import { useState, useEffect } from 'react'
import { useLoaderData } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { ProgressSpinner } from 'primereact/progressspinner'
import ContentSection from '../../components/common/ContentSection/ContentSection'
import { StatsTopBarSelector } from '../../components/Stats/StatsTopBarSelector'
import { StatsFilter }         from '../../components/Stats/filters/StatsFilter'
import { StatsChart }          from '../../components/Stats/charts/StatsChart'
import { StatsChartInsights }  from '../../components/Stats/StatsChartInsights'
import { useStatsFilters }   from '../../hooks/useStatsFilters'
import { useStatsChartData } from '../../hooks/useStatsChartData'
import type { ChartType }     from '../../types/Stats'
import { isInsights } from '../../utils/insights'
import { useLocalizationLabels } from '../../context/LocalizationLabelContext'
import '../../components/Stats/charts/Chart.css'
import './Stats.css'

function Stats() {
  const regions = useLoaderData() as string[]
  const { t } = useTranslation('mainStats')
  const { statsLabels } = useLocalizationLabels()
  const {
    scope, geo, metric,
    filtersResp, chartType, loadingFilters, filtersError,
    pendingFilters, appliedFilters,
    availableCompareGeo, setAvailableCompareGeo,
    setChartType,
    handleScopeChange, handleGeoChange, handleMetricChange,
    handleSourceChange, handlePendingChange, handleApply,
    handleCompareGeoChange,
  } = useStatsFilters(regions)
  
  const {
    orderedData,
    loading, dataError, isEmpty,
    hasChart, isRefreshing, missingCompareGeo,
    sourceUrl, sourceName,
    licenseName, licenseUrl,
    chartTypes,
    insights,
  } = useStatsChartData(scope, geo, metric, appliedFilters, filtersResp, setAvailableCompareGeo)


  const [isPageReady, setIsPageReady] = useState(false)

  useEffect(() => {
    if (isPageReady) return
    const filtersDone = !loadingFilters && !!filtersResp && !!appliedFilters.source
    const chartDone   = !loading && (hasChart || isEmpty || dataError)
    const ready       = filtersError || (filtersDone && chartDone)

    if (ready) setIsPageReady(true)
  }, [isPageReady, filtersError, filtersResp, loadingFilters, loading, hasChart, isEmpty, dataError, appliedFilters.source])

  if (!isPageReady) {
    return (
      <div className="analysis-page analysis-page--loading">
        <ProgressSpinner />
      </div>
    )
  }

  return (
    <ContentSection
      title={t('selector.availableAnalyses')}
      level={1}
      subtitle={t('selector.availableAnalysesTooltip')}
      contentClassName="analysis-page"
    >
      <StatsTopBarSelector
        regions={regions}
        scope={scope}          onScopeChange={handleScopeChange}
        geo={geo}              onGeoChange={handleGeoChange}
        metric={metric}        onMetricChange={handleMetricChange}
        sources={filtersResp?.sources ?? []}
        currentSource={appliedFilters.source}
        onSourceChange={handleSourceChange}
      />
      {loadingFilters && <p className="loading">{t('chart.loading')}</p>}
      {filtersError && !loadingFilters && (
        <Message severity="error" text={t('selector.loadError')} className="page-error" />
      )}
      {filtersResp && !loadingFilters && (
        <StatsFilter
          filtersResp={filtersResp}
          pendingFilters={pendingFilters}
          appliedFilters={appliedFilters}
          availableCompareGeo={availableCompareGeo}
          onPendingChange={handlePendingChange}
          onCompareGeoChange={handleCompareGeoChange}
          onApply={handleApply}
        />
      )}
      {filtersResp && metric && !loadingFilters && (
        <div className="stats-chart-wrapper">
          <div className="chart-type-selector">
            {chartTypes.map(ct => (
              <Button
                key={ct}
                label={t(`chart.type${ct.charAt(0).toUpperCase() + ct.slice(1)}`, { defaultValue: ct })}
                onClick={() => setChartType(ct as ChartType)}
                outlined={chartType !== ct}
                size="small"
              />
            ))}
          </div>
          {loading && !hasChart && <p className="loading">{t('chart.loading')}</p>}
          {!loading && dataError && (
            <Message severity="error" text={t('chart.dataError')} className="chart-message" />
          )}
          {!loading && !dataError && isEmpty && (
            <Message severity="info" text={t('chart.emptyData')} className="chart-message" />
          )}
          {hasChart && (
            <div style={{ opacity: isRefreshing ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              <StatsChart
                type={chartType}
                data={orderedData!}
                yearRange={appliedFilters.yearRange}
                metricName={metric ? (statsLabels.metrics?.[metric]?.name ?? metric) : ''}
                unit={appliedFilters.unit}
                sourceUrl={sourceUrl}
                sourceName={sourceName}
                licenseName={licenseName}
                licenseUrl={licenseUrl}
              />

              {!isRefreshing && missingCompareGeo.length > 0 && (
                <Message
                  severity="warn"
                  text={t('chart.noDataForTerritories', { territories: missingCompareGeo.join(', ') })}
                  className="chart-message"
                />
              )}
              {orderedData && (
                <StatsChartInsights
                  geoOrder={Object.keys(orderedData)}
                  insights={isInsights(insights) ? insights : null}
                  yearRange={appliedFilters.yearRange}
                  metric={metric}
                  unit={appliedFilters.unit}
                  scope={scope}
                  geo={geo}
                  appliedFilters={appliedFilters}
                />
              )}
              <h3 className="metric-descp-text data-disclosure-footer">{t('footer.dataDisclosure')}</h3>
            </div>
          )}
        </div>
      )}
    </ContentSection>
  )
}

export { Stats }
